import { MERCHANT_NAME, UEN_NUMBER } from "@/constants/invoices";
import { _nations } from "@/constants/nation";
import { ApolloError } from "@apollo/client";

export const GST_REGISTRATION = "201727751R";

export const fullName = ({
  firstName,
  lastName,
}: {
  firstName: string;
  lastName: string;
}): string => {
  return `${firstName} ${lastName}`;
};

export function extractTextAfterColon(input: string) {
  const colonPosition = input.indexOf(":");
  return colonPosition !== -1 ? input.slice(colonPosition + 1).trim() : input;
}

export const getUnitNumber = (
  unitNumber: string | undefined,
): { unitFloor: string; apartmentNumber: string } => {
  if (!unitNumber) return { unitFloor: "", apartmentNumber: "" };

  const [floorPart, apartmentNumber = ""] = unitNumber.split("-");
  const unitFloor = floorPart.split("#")[1] ?? floorPart;

  return { unitFloor, apartmentNumber };
};

export const createUnitNumber = (unitFloor?: string, apartmentNumber?: string) => {
  const floor = unitFloor === undefined ? "" : unitFloor;
  const apt = apartmentNumber === undefined ? "" : apartmentNumber;

  if ((floor === "" && apt === "") || (floor === "0" && apt === "0")) return "";

  if (floor === "") return `#NA-${apt}`;
  if (apt === "") return `#${floor}-NA`;

  return `#${floor}-${apt}`;
};

const getNationInfo = (phoneNumber: string) => {
  const fullNumber = phoneNumber.startsWith("+") ? phoneNumber.slice(1) : phoneNumber;
  return _nations.find((nation) => fullNumber.startsWith(nation.tel));
};

export const getNationCode = (phoneNumber: string): string => {
  const nationInfo = getNationInfo(phoneNumber);

  if (nationInfo) {
    return `${nationInfo.short}/${nationInfo.tel}`;
  }

  return "";
};

export const getPhoneNumber = (phoneNumber: string): string => {
  const nationInfo = getNationInfo(phoneNumber);

  if (nationInfo) {
    return phoneNumber.startsWith("+")
      ? phoneNumber.slice(1 + nationInfo.tel.length)
      : phoneNumber.slice(nationInfo.tel.length);
  }

  return phoneNumber.startsWith("+") ? phoneNumber.slice(1) : phoneNumber;
};

export const ensurePhoneNumberWithPlus = (phoneNumber: string): string => {
  return phoneNumber.startsWith("+") ? phoneNumber : `+${phoneNumber}`;
};

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const getErrorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof ApolloError) {
    if (err.graphQLErrors.length > 0) {
      return err.graphQLErrors[0].message;
    } else if (err.networkError) {
      return err.networkError.message;
    }
  }

  if (err instanceof Error) {
    return err.message;
  }

  return fallback;
};

export const formatText = (str?: string | null) => {
  if (!str) return "";
  return str
    .split("_")
    .map((str) => capitalizeFirstLetter(str))
    .join(" ");
};

export function generateWALink(phone: string, message: string) {
  return "//wa.me/" + phone + "?text=" + message;
}

export function cleanupNumber(number: string): string {
  return number.replace(/\D/g, "");
}

export function getUserNameShortCut({
  firstName,
  lastName,
}: {
  firstName: string;
  lastName: string;
}) {
  return [firstName, lastName].map((name) => (name && name.length > 0 ? name[0] : "")).join("");
}

export function generatePaymentQRUrl({
  invoiceId,
  balance,
}: {
  invoiceId: string;
  balance: number;
}) {
  return `https://sslsecure.maybank.com.sg/scripts/mbb_qrcode/mbb_qrcode.jsp?proxyValue=${UEN_NUMBER}&proxyType=UEN&merchantName=${MERCHANT_NAME}&amount=${balance}&reference=${invoiceId}`;
}

export const sanitizeOptionalText = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

export function extractTokenFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get("token");
  } catch (error) {
    console.error("Invalid URL:", error);
    return null;
  }
}

export function formatClientNameHide(name: string): string {
  // our client name data base sometimes have spacing in the front of it
  // so we need to remove leading whitespace
  return name
    .split("\n")
    .map((line) => {
      line = line.trim();
      const words = line.split(" ").filter(Boolean);
      if (words.length > 2) {
        return words[0] + " " + words[1][0] + "***";
      }
      return words[0] + " " + (words[1] ? words[1][0] + "***" : "");
    })
    .join("\n");
}
